import { supabase } from '../supabaseClient';
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';

export interface Alarm {
  id: string;
  company_id: string;
  site_id: string | null;
  shift_assignment_id: string | null;
  triggered_by: string | null;
  title: string | null;
  message: string | null;
  status: 'open' | 'acknowledged' | 'resolved' | 'cancelled';
  triggered_at: string;
  resolved_at: string | null;
  metadata?: {
    source?: string;
    welfare_check_id?: string;
    scheduled_at?: string;
    response_due_at?: string;
    [key: string]: unknown;
  } | null;
  profiles?: { full_name: string | null; avatar_url: string | null } | null;
  /** `location` is only selected for active alarms, as the live map's last-resort position. */
  sites?: { name: string; location?: unknown } | null;
  /** Id of the report auto-generated for this alarm, when one exists. */
  report_id?: string | null;
}

/** The verified GPS ping a guard's handset sent when it raised a panic alarm. */
export interface AlarmLocation {
  alarmId: string;
  latitude: number;
  longitude: number;
  accuracyM: number | null;
  recordedAt: string;
}

type AlarmLocationRecord = {
  alarm_id: string;
  latitude: number;
  longitude: number;
  accuracy_m: number | null;
  recorded_at: string;
};

function toAlarmLocation(record: AlarmLocationRecord): AlarmLocation {
  return {
    alarmId: record.alarm_id,
    latitude: record.latitude,
    longitude: record.longitude,
    accuracyM: record.accuracy_m,
    recordedAt: record.recorded_at,
  };
}

export const alarmService = {
  async fetchActiveAlarms(companyId: string): Promise<Alarm[]> {
    const { data, error } = await supabase
      .from('alarms')
      .select(`
        *,
        profiles:triggered_by(full_name, avatar_url),
        sites:site_id(name, location)
      `)
      .eq('company_id', companyId)
      .in('status', ['open', 'acknowledged'])
      .order('triggered_at', { ascending: false });

    if (error) throw error;
    return (data ?? []) as Alarm[];
  },

  /**
   * Trigger pings for the given alarms. Only panic alarms have one — a missed
   * welfare check never does — and it is written moments after the alarm row,
   * so a missing entry is normal rather than an error.
   */
  async fetchAlarmLocations(alarmIds: string[]): Promise<AlarmLocation[]> {
    if (alarmIds.length === 0) return [];

    const { data, error } = await supabase
      .from('alarm_locations')
      .select('alarm_id, latitude, longitude, accuracy_m, recorded_at')
      .in('alarm_id', alarmIds);

    if (error) throw error;
    return ((data ?? []) as AlarmLocationRecord[]).map(toAlarmLocation);
  },

  async fetchAlarmHistory(companyId: string, limit = 50): Promise<Alarm[]> {
    const { data, error } = await supabase
      .from('alarms')
      .select(`
        *,
        profiles:triggered_by(full_name, avatar_url),
        sites:site_id(name),
        reports:reports!reports_alarm_id_fkey(id)
      `)
      .eq('company_id', companyId)
      .in('status', ['resolved', 'cancelled'])
      .order('triggered_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    // Each alarm auto-generates one report (trigger create_report_from_alarm),
    // but the embed comes back as an array — flatten it to a single id.
    return ((data ?? []) as Array<Alarm & { reports?: { id: string }[] | null }>).map(
      ({ reports, ...alarm }) => ({ ...alarm, report_id: reports?.[0]?.id ?? null }),
    );
  },

  async acknowledgeAlarm(alarmId: string): Promise<void> {
    const { error } = await supabase
      .from('alarms')
      .update({ status: 'acknowledged' })
      .eq('id', alarmId);
    if (error) throw error;
  },

  async resolveAlarm(alarmId: string): Promise<void> {
    const { error } = await supabase
      .from('alarms')
      .update({ status: 'resolved', resolved_at: new Date().toISOString() })
      .eq('id', alarmId);
    if (error) throw error;
  },

  subscribeToAlarms(
    companyId: string,
    callback: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void,
  ): () => void {
    const channel = supabase
      .channel(`alarms_company_${companyId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'alarms',
          filter: `company_id=eq.${companyId}`,
        },
        callback,
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  },
};
